import { getDomainTags, getPlatformsList } from '@/apis/card/index';
import { getApiLang } from '@/i18n';
import type { AdvertiserFieldItem } from '@/utils/advertiserFields';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface PlatformOption {
  value: string;
  name: string;
}

export const useAdvertiserMetaStore = defineStore('advertiserMeta', () => {
  const platforms = ref<PlatformOption[]>([]);
  const fieldsRaw = ref<AdvertiserFieldItem[]>([]);
  const platformsLoaded = ref(false);
  const domainTagsLoaded = ref(false);

  let platformsInflight: Promise<PlatformOption[]> | null = null;
  let domainTagsInflight: Promise<AdvertiserFieldItem[]> | null = null;

  const loadPlatforms = async (force = false) => {
    if (!force && platformsLoaded.value) {
      return platforms.value;
    }
    if (!force && platformsInflight) {
      return platformsInflight;
    }

    platformsInflight = getPlatformsList(undefined, { hideLoading: true })
      .then((data: any) => {
        platforms.value = (data || []).map(
          ({
            platformId: value,
            platformName: name,
          }: {
            platformId: string;
            platformName: string;
          }) => ({ value, name }),
        );
        platformsLoaded.value = true;
        return platforms.value;
      })
      .finally(() => {
        platformsInflight = null;
      });

    return platformsInflight;
  };

  const loadDomainTags = async (force = false) => {
    if (!force && domainTagsLoaded.value) {
      return fieldsRaw.value;
    }
    if (!force && domainTagsInflight) {
      return domainTagsInflight;
    }

    domainTagsInflight = getDomainTags({ lang: getApiLang() }, { hideLoading: true })
      .then((data: AdvertiserFieldItem[]) => {
        fieldsRaw.value = data || [];
        domainTagsLoaded.value = true;
        return fieldsRaw.value;
      })
      .finally(() => {
        domainTagsInflight = null;
      });

    return domainTagsInflight;
  };

  const invalidate = () => {
    platforms.value = [];
    fieldsRaw.value = [];
    platformsLoaded.value = false;
    domainTagsLoaded.value = false;
    platformsInflight = null;
    domainTagsInflight = null;
  };

  return {
    platforms,
    fieldsRaw,
    platformsLoaded,
    domainTagsLoaded,
    loadPlatforms,
    loadDomainTags,
    invalidate,
  };
});
